import React, { useState, useEffect } from "react";
import AvailabilityRequestsTableRow from "./AvailabilityRequestsTableRow.js";
import EmptyState from "../../shared/helpers/EmptyState.js";
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

export default function AvailabilityRequestsTable(props) {

  // Sorting state - default to earliest effective date first
  const [sortField, setSortField] = useState('effective_date');
  const [sortDirection, setSortDirection] = useState('asc');
  const [sortedRequests, setSortedRequests] = useState([]);

  const hasAvailabilityRequests = props.availabilityRequestsData.length > 0;

  // Initial sort and whenever props change
  useEffect(() => {
    if (props.availabilityRequestsData && props.availabilityRequestsData.length > 0) {
      const sorted = sortRequests(props.availabilityRequestsData, sortField, sortDirection);
      setSortedRequests(sorted);
    } else {
      setSortedRequests([]);
    }
  }, [props.availabilityRequestsData, sortField, sortDirection]);

  // Handle sort header click
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if same field clicked
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Function to sort requests based on field and direction
  const sortRequests = (requests, field, direction) => {
    return [...requests].sort((a, b) => {
      let compareA, compareB;

      // Get the right values based on field
      switch (field) {
        case 'employee':
          compareA = a.name.toLowerCase();
          compareB = b.name.toLowerCase();
          break;
        case 'effective_date': {
          const fallback = direction === 'asc'
            ? Number.POSITIVE_INFINITY
            : Number.NEGATIVE_INFINITY;

          const getStatusRank = (request) => {
            const status = request?.requested?.status?.toLowerCase();
            switch (status) {
              case 'approved':
                return 0;
              case 'denied':
                return 1;
              case 'pending':
                return 2;
              default:
                return 3;
            }
          };

          const getCreatedAt = (request) => {
            const timestamp = request.requested?.created_at || request.created_at;
            const timeValue = timestamp ? new Date(timestamp).getTime() : null;
            return Number.isFinite(timeValue) ? timeValue : null;
          };

          const dateA = a.requested?.inEffect
            ? new Date(a.requested.inEffect).getTime()
            : fallback;
          const dateB = b.requested?.inEffect
            ? new Date(b.requested.inEffect).getTime()
            : fallback;

          compareA = Number.isFinite(dateA) ? dateA : fallback;
          compareB = Number.isFinite(dateB) ? dateB : fallback;

          if (compareA === compareB) {
            const statusDiff = getStatusRank(a) - getStatusRank(b);
            if (statusDiff !== 0) {
              return direction === 'asc' ? statusDiff : -statusDiff;
            }

            const createdDiff = (getCreatedAt(a) || 0) - (getCreatedAt(b) || 0);
            if (createdDiff !== 0) {
              return direction === 'asc' ? createdDiff : -createdDiff;
            }
          }
          break;
        }
        case 'created_at':
          compareA = new Date(a.requested.created_at || a.created_at);
          compareB = new Date(b.requested.created_at || b.created_at);
          break;
        default:
          compareA = a[field];
          compareB = b[field];
      }

      // Compare the values based on direction
      if (direction === 'asc') {
        if (compareA < compareB) return -1;
        if (compareA > compareB) return 1;
        return 0;
      } else {
        if (compareA > compareB) return -1;
        if (compareA < compareB) return 1;
        return 0;
      }
    });
  };

  // Table header with sort indicators
  const SortableHeader = ({ field, title, width, className = "", rightAlign = false }) => {
    return (
      <th
        scope="col"
        className={`${width} px-3 py-3 ${rightAlign ? 'text-right' : 'text-left'} text-xs font-semibold text-slate-500 cursor-pointer hover:text-slate-700 group ${className}`}
        onClick={() => handleSort(field)}
      >
        <div className={`flex items-center space-x-1 ${rightAlign ? 'justify-end' : ''}`}>
          <span>{title}</span>
          <div className="flex flex-col">
            {sortField === field ? (
              sortDirection === 'asc' ? (
                <ArrowUpwardIcon sx={{ fontSize: 14 }} />
              ) : (
                <ArrowDownwardIcon sx={{ fontSize: 14 }} />
              )
            ) : (
              <span className="opacity-0 group-hover:opacity-30">
                <ArrowUpwardIcon sx={{ fontSize: 14 }} />
              </span>
            )}
          </div>
        </div>
      </th>
    );
  };

  return (
    <>
      {hasAvailabilityRequests ? (
        <div className="mt-12">
          <div className="flex items-center px-6">
            <div className="flex-auto">
              <h1 className="text-xl font-bold text-slate-900">Requests</h1>
            </div>
          </div>
          <div className="mt-8 flex flex-col">
            <div className="-my-2 overflow-x-auto min-w-full">
              <div className="inline-block min-w-full py-2 align-middle">
                <div className="overflow-hidden">
                  <table className="min-w-full divide-y divide-slate-100">
                    <thead>
                      <tr>
                        <SortableHeader
                          field="employee"
                          title="Employee"
                          width="w-[20%]"
                          className="pl-6 pr-3"
                        />
                        <SortableHeader
                          field="created_at"
                          title="Submitted"
                          width="w-[16%]"
                        />
                        <th
                          scope="col"
                          className="w-[14%] px-3 py-3 text-left text-xs font-semibold text-slate-500"
                        >
                          Day
                        </th>
                        <th
                          scope="col"
                          className="w-[17%] px-3 py-3 text-left text-xs font-semibold text-slate-500"
                        >
                          Existing Availability
                        </th>
                        <th
                          scope="col"
                          className="w-[17%] px-3 py-3 text-left text-xs font-semibold text-slate-500"
                        >
                          Requested Availability
                        </th>
                        <SortableHeader
                          field="effective_date"
                          title="Effective Date"
                          width="w-[18%]"
                          className="pl-3 pr-3"
                          rightAlign={true}
                        />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {sortedRequests.map((request) => (
                        <AvailabilityRequestsTableRow
                          key={request.requested.id}
                          name={request.name}
                          day={request.day}
                          currentAvailability={request.availability}
                          currentAvailability2={request.availability2}
                          currentPreferences={request.preferences}
                          requestedAvailability={request.requested.availability}
                          requestedAvailability2={
                            request.requested.availability2
                          }
                          requestedPreferences={request.requested.preferences}
                          inEffect={request.requested.inEffect}
                          id={request.requested.id}
                          note={request.requested.note}
                          manager_respond_to_availability_request={
                            props.manager_respond_to_availability_request
                          }
                          dateSubmitted={request.requested.created_at}
                          storeTimezone={props.storeTimezone}
                        />
                      ))}
                    </tbody>
                  </table>
                  <div className="border-b border-slate-100"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <EmptyState
          title="Requests"
          description="No availability requests at the moment"
        />
      )}
    </>
  );
}
